import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { fetchExperimentCategoryPerformance } from '../services/experiment.service';
import { EXPERIMENT_ANALYTICS_READ_OPTIONS } from '../utils/snapshot-read-retry';

/**
 * Single fetch returning every piece of data the category-mode UI needs —
 * chatbot Overall-DID-lift + Standouts on the left, KPI row + Weekly DiD
 * Revenue charts on the right. Per-category dropdown swaps drive the same
 * fetch via the `category` arg.
 *
 * `keepPreviousData` keeps the previous chart visible while a new category
 * loads, so ECharts doesn't unmount and remount (which would flicker the
 * Skeleton and dispose the chart instance).
 *
 * Pass `{ enabled: false }` to skip the network when the consumer isn't in
 * category mode (e.g. the chatbot subscribes only when `performanceMode === 'category'`).
 */
export function useExperimentCategoryPerformance(
  experimentId: string,
  category: string,
  options?: { enabled?: boolean | undefined; segment?: string | undefined },
) {
  return useQuery({
    queryKey: queryKeys.experiments.categoryPerformance(experimentId, category, options?.segment),
    queryFn: ({ signal }) =>
      fetchExperimentCategoryPerformance(experimentId, category, options?.segment, signal),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
    // Snapshot-backed analytics read: shared retry/backoff policy. See helper.
    ...EXPERIMENT_ANALYTICS_READ_OPTIONS,
  });
}
